import React from 'react';
import {
  ArrowRight, Briefcase, CalendarDays, CheckCircle2, Coffee, LockOpen, LucideIcon, Moon, Pencil, Play, Send, Square,
} from 'lucide-react';
import { actorLine, miniLine, toneOf } from './activityModel';
import { fmtTime } from './portalTime';
import { ActivitySrcChip } from './SrcChip';
import type { PortalActivityItem } from './portalTypes';

/**
 * One activity feed row (handoff portal.js auditRow): time · icon · what + who · detail ·
 * before → after · source chip. `mini` is the compact form for Today's Recent activity and
 * My Timesheet's "Changes to this week" (date + time in the sub-line, chip label inline).
 */

export function activityIcon(item: Pick<PortalActivityItem, 'kind' | 'what'>): LucideIcon {
  if (/^split at midnight/i.test(item.what)) return Moon;
  switch (item.kind) {
    case 'punch':
      if (/meal/i.test(item.what)) return Coffee;
      if (/^switched/i.test(item.what)) return Briefcase;
      return /out/i.test(item.what) ? Square : Play;
    case 'edit': return Pencil;
    case 'submit': return Send;
    case 'approve': return CheckCircle2;
    case 'unlock': return LockOpen;
    case 'timeoff': return CalendarDays;
  }
  return Pencil;
}

export const DeltaChip: React.FC<{ before: string; after: string }> = ({ before, after }) => (
  <span className="delta"><span className="was" title={before}>{before}</span><ArrowRight /><span className="now" title={after}>{after}</span></span>
);

const ActivityRow: React.FC<{ item: PortalActivityItem; zone: string; mini?: boolean; todayYmd?: string }> = ({ item, zone, mini, todayYmd }) => {
  const Icon = activityIcon(item);
  const detail = item.detail.trim();
  if (mini) {
    return (
      <div className="aud-row">
        <span className={`aud-ic ${toneOf(item.kind)}`}><Icon /></span>
        <span className="aud-b">
          <span className="at">{item.what}</span>
          <span className="as" style={{ display: 'block' }} title={detail || undefined}>{miniLine(item, zone, todayYmd ?? '')}</span>
          {item.delta && <DeltaChip before={item.delta.before} after={item.delta.after} />}
        </span>
      </div>
    );
  }
  return (
    <div className="aud-row">
      <span className="aud-t">{fmtTime(Date.parse(item.at), zone)}</span>
      <span className={`aud-ic ${toneOf(item.kind)}`}><Icon /></span>
      <span className="aud-b">
        <span className="at">{item.what}<span className="who">{actorLine(item)}</span></span>
        {detail && <span className="as wrap" style={{ display: 'block' }}>{detail}</span>}
        {item.delta && <DeltaChip before={item.delta.before} after={item.delta.after} />}
      </span>
      <span className="aud-src"><ActivitySrcChip type={item.source.type} label={item.source.label} /></span>
    </div>
  );
};

export default ActivityRow;
